import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { postData, getData } from '../../Api';
import TwButton from '../../controls/button';

function Login() {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [captchaToken, setCaptchaToken] = useState('');
    const [captchaCode, setCaptchaCode] = useState('');
    const [captchaImage, setCaptchaImage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [theme, setTheme] = useState(window.localStorage.getItem('pargar-panel-theme') || 'light');

    const changeTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        window.localStorage.setItem('pargar-panel-theme', newTheme);
        themeInit(newTheme);
    }

    const themeInit = (theme) => {
        document.documentElement.classList.remove('light');
        document.documentElement.classList.remove('dark');
        document.documentElement.classList.add(theme);
    }

    useEffect(() => {
        themeInit(theme);
    }, [theme]);

    const refreshCaptcha = async () => {
        try {
            const result = await getData('/api/auth/captcha');
            if (result.success) {
                setCaptchaToken(result.token);
                setCaptchaImage(result.image);
                setCaptchaCode('');
            }
        } catch (error) {
            setError('خطا در دریافت تصویر امنیتی');
        }
    };

    useEffect(() => {
        refreshCaptcha();
    }, []);

    const validateCaptcha = async () => {
        try {
            const result = await postData('/api/auth/captcha-validate', {
                token: captchaToken,
                code: captchaCode,
            });
            return result.success;
        } catch (error) {
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // First validate captcha
            const isCaptchaValid = await validateCaptcha();
            if (!isCaptchaValid) {
                setError('کد امنیتی نادرست است');
                await refreshCaptcha();
                setLoading(false);
                return;
            }

            // Then attempt login
            const result = await postData('/api/auth/login-with-password', {
                username,
                password,
                captchatoken: captchaToken,
            });
            console.log(result);

            if (result.success) {
                const token = result.token;
                localStorage.setItem('admin_auth_token', token);
                // const AUTHENTICATION_EXPIRE_MINUTES = 60 * 24 * 30;
                // document.cookie = `taroff-authentication-token=${token}; path=/; max-age=${AUTHENTICATION_EXPIRE_MINUTES * 60};`;
                navigate('/admin/site-admin');
            } else {
                setError(result.error || 'خطا در ورود به سیستم');
                await refreshCaptcha();
            }
        } catch (error) {
            setError(error.message || 'خطا در ورود به سیستم');
            await refreshCaptcha();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            dir="rtl"
            style={{
                backgroundImage: theme === 'dark' ? `linear-gradient(to bottom, #00000099, #000000fe), url(${process.env.PUBLIC_URL}/taroff-back.jpg)` : `linear-gradient(to bottom, #ffffff99, #fffffffe), url(${process.env.PUBLIC_URL}/taroff-back.jpg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
            className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8"
        >
            <div className="max-w-sm px-4 py-8 dark:text-white bg-white dark:bg-zinc-900 rounded-md shadow-md border border-gray-200 w-full space-y-8 relative">
                <div onClick={() => changeTheme()} className="absolute top-4 left-0 flex items-center gap-x-3 p-2 rounded-md text-sm font-semibold leading-6 text-gray-700 dark:text-gray-400">
                    <i className={(theme === 'dark' ? 'fa-sun' : 'fa-moon') + ' fal fa-fw fa-lg'}></i>
                </div>
                <div>
                    <img
                        className="mx-auto h-24 w-auto"
                        src={`${process.env.PUBLIC_URL}/taroff-logo.png`}
                        alt="Logo"
                    />
                    <h2 className="mt-6 text-center text-3xl font-extrabold">
                        ورود به سیستم
                    </h2>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label htmlFor="username" className="sr-only">
                                نام کاربری
                            </label>
                            <input
                                id="username"
                                name="username"
                                dir="ltr"
                                type="text"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm bg-white dark:bg-gray-800"
                                placeholder="نام کاربری"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">
                                رمز عبور
                            </label>
                            <input
                                id="password"
                                name="password"
                                dir="ltr"
                                type="password"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm bg-white dark:bg-gray-800"
                                placeholder="رمز عبور"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <img
                            src={captchaImage}
                            alt="Captcha"
                            className="h-24 w-full object-contain bg-white rounded-t-md mt-2"
                        />
                        <input
                            type="text"
                            required
                            className="appearance-none rounded-b-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm bg-white dark:bg-gray-800"
                            placeholder="کد امنیتی"
                            value={captchaCode}
                            onChange={(e) => setCaptchaCode(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={refreshCaptcha}
                            className="text-sm mt-2 text-zinc-400"
                        >
                            تازه سازی تصویر امنیتی
                        </button>
                    </div>

                    {error && <div className="text-red-500 text-sm text-center">{error}</div>}

                    <div>
                        <TwButton
                            type="submit"
                            color="primary"
                            style="solid"
                            className="w-full"
                            disabled={loading}
                        >
                            {loading ? 'در حال ورود...' : 'ورود'}
                        </TwButton>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default Login;
